import { LockClosedIcon } from "@heroicons/react/24/outline"
import { Button } from "components/core/button"
import { Divider } from "components/core/divider"
import { Link } from "components/core/link"
import { Tooltip } from "components/core/tooltip"
import { UserAvatar } from "components/layout/header/userMenu"
import { useSubscription } from "components/user/useSubscription"
import { Dashboard } from "domainModels/dashboard"
import { FeatureName } from "lib/features/features"
import { useFeature } from "lib/features/useFeature"
import { useUser } from "lib/supabase/auth"
import { getDashboardSlug } from "lib/utils"
import { useRouter } from "next/router"
import { FC } from "react"
import { useForkDashboard, useSelectDashboards } from "reactQuery/dashboard"
import { useSelectGroups } from "reactQuery/group"
import { useSelectPublicWidgets, useSelectWidgets } from "reactQuery/widget"

interface Props {
  dashboard: Dashboard
}

export const Fork: FC<Props> = ({ dashboard }) => {
  const { replace } = useRouter()
  const { user, userDetails } = useUser()
  const { exhausted } = useFeature(FeatureName.DASHBOARD_AMOUNT)
  const { mutateAsync: forkDashboard } = useForkDashboard()
  const { data: subscription, isLoading: isLoadingSubscription } = useSubscription()
  const { data: dashboards, isLoading: isLoadingDashboards } = useSelectDashboards()
  const { isLoading: isLoadingPublicWidgets } = useSelectPublicWidgets()
  const { data: widgets, isLoading: isLoadingWidgets } = useSelectWidgets(dashboard.id)
  const { data: groups, isLoading: isLoadingGroups } = useSelectGroups(dashboard.id)
  const hasPrimaryDashboard = !!dashboards?.find((d) => d.is_primary)
  const isLoading =
    isLoadingWidgets || isLoadingGroups || isLoadingDashboards || isLoadingSubscription || isLoadingPublicWidgets
  const forkButton = (
    <Button
      disabled={!user}
      size="small"
      className="text-sm"
      onClick={async () => {
        if (user && userDetails && widgets) {
          const forkedDashboard = await forkDashboard({
            dashboard: new Dashboard({
              ...dashboard,
              id: undefined,
              is_primary: !hasPrimaryDashboard,
              is_forked: true,
              user_id: user.id,
              name: `[Forked] ${dashboard.name}`,
              owner_name: userDetails.full_name,
              owner_avatar: userDetails.avatar_url,
            }),
            widgets,
            groups: groups ?? [],
          })

          if (forkedDashboard?.id) {
            replace("/dashboard/[slug]", `/dashboard/${getDashboardSlug(forkedDashboard.id, user.id)}`)
          }
        }
      }}
    >
      {user && exhausted && <LockClosedIcon className="w-4 h-4 mr-2" />}
      Fork this dashboard
    </Button>
  )

  if (isLoading) {
    return null
  }

  return (
    <div className="text-sm px-4 sm:px-6 lg:px-12 pt-4 text-gray-700 dark:text-gray-300">
      <div className="sm:flex items-center">
        <div className="flex items-center">
          <UserAvatar avatar_url={dashboard.owner_avatar} alt={dashboard.owner_name} />

          <div className="ml-2 flex">
            <p className="leading-tight font-medium text-gray-900 dark:text-gray-300 truncate max-w-[200px]">
              {dashboard.name}
            </p>
            <span className="mx-2 text-gray-300">/</span>
            <p className="leading-tight text-gray-500 dark:text-gray-400" title={dashboard.owner_name}>
              by {dashboard.owner_name}
            </p>
          </div>
        </div>

        {!dashboard.is_private && (
          <div className="mt-2 ml-7 md:mt-0 sm:ml-6 sm:pl-6 sm:border-l border-gray-200 dark:border-gray-800">
            {user && !exhausted ? (
              forkButton
            ) : (
              <Tooltip
                interactive
                content={
                  user ? (
                    <>
                      You can&apos;t add more dashboards with your current plan.{" "}
                      {!subscription && (
                        <Link to="/upgrade" className="block">
                          Upgrade now
                        </Link>
                      )}
                    </>
                  ) : (
                    <>
                      <Link to="/auth/login" className="block">
                        Sign in
                      </Link>{" "}
                      to fork this dashboard
                    </>
                  )
                }
              >
                {forkButton}
              </Tooltip>
            )}
          </div>
        )}
      </div>
      <Divider className="mt-4" />
    </div>
  )
}
